import React, { createContext, useState, useContext, ReactNode } from 'react'


interface WebSocketContextProps {
  message: string
  percentage: number
  close: boolean
  status: 'error' | 'info' | 'success' | 'warning'
  setStatus: (status: 'error' | 'info' | 'success' | 'warning') => void
  setMessage: (message: string) => void
  setPercentage: (message: number) => void
  setClose: (close: boolean) => void
}

/**
 * Contexto de WebSocket que proporciona acceso a valores y funciones para gestionar el estado de WebSocket.
 * 
 * @type {React.Context<WebSocketContextProps>}
 * 
 * @example
 * ```tsx
 * const { message, status, setMessage } = useContext(WebSocketContext);
 * ```
 */
export const WebSocketContext = createContext<WebSocketContextProps>({
  message: '',
  percentage: 0,
  close: false,
  status: 'info',
  setStatus: () => { },
  setMessage: () => { },
  setPercentage: () => { },
  setClose: () => { },
})

/**
 * Proveedor de WebSocket que maneja el estado y expone el contexto a sus componentes hijos.
 * 
 * @component
 * @param {Object} props - Propiedades del componente.
 * @param {ReactNode} props.children - Elementos hijos que serán envueltos por el proveedor de WebSocket.
 * @returns {JSX.Element} Componente proveedor de WebSocket.
 * 
 * @example
 * ```tsx
 * import { WebSocketProvider } from './WebSocketContext';
 * 
 * const App = () => (
 *   <WebSocketProvider>
 *     <YourComponent />
 *   </WebSocketProvider>
 * );
 * ```
 */
export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [message, setMessage] = useState<string>('')
  const [percentage, setPercentage] = useState<number>(0)
  const [close, setClose] = useState<boolean>(false)
  const [status, setStatus] = useState<'error' | 'info' | 'success' | 'warning'>('info')

  return (
    <WebSocketContext.Provider
      value={{
        message,
        percentage,
        close,
        status,
        setStatus,
        setMessage,
        setPercentage,
        setClose,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}
